import { NavLink, useNavigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { SocialShare } from "@/components/SocialShare";
import {
  Home, ScanLine, Route, Wrench, MoreHorizontal, MessageCircle,
  Heart, Wallet, ShieldAlert, ShieldCheck, User, Crown, LogOut, LayoutDashboard, FileText, ParkingSquare,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const bottomNav = [
  { to: "/app", label: "Home", icon: Home, end: true },
  { to: "/app/scan", label: "Scan", icon: ScanLine },
  { to: "/app/drive", label: "Drive", icon: Route },
  { to: "/app/care", label: "Care", icon: Wrench },
  { to: "/app/more", label: "More", icon: MoreHorizontal },
];

const sideNav = [
  { to: "/app", label: "Dashboard", icon: Home, end: true },
  { to: "/app/scan", label: "AI Car Scan", icon: ScanLine },
  { to: "/app/assistant", label: "Ask VecPadi", icon: MessageCircle },
  { to: "/app/health", label: "Vehicle Health", icon: Heart },
  { to: "/app/drive", label: "Drive & Traffic", icon: Route },
  { to: "/app/care", label: "Care & Documents", icon: Wrench },
  { to: "/app/expenses", label: "Expenses", icon: Wallet },
  { to: "/app/parking", label: "Parking", icon: ParkingSquare },
  { to: "/app/safety", label: "Safety & SOS", icon: ShieldAlert },
  { to: "/app/insurance", label: "Insurance", icon: ShieldCheck },
  { to: "/app/profile", label: "Profile", icon: User },
];

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onAssistant = location.pathname.includes("/assistant");

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-[260px] flex-col border-r border-zinc-200 bg-white px-4 py-6 z-30">
        <div className="px-2">
          <Logo />
        </div>
        <nav className="mt-8 flex-1 space-y-1 overflow-y-auto no-scrollbar">
          {sideNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`side-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900"
                }`
              }
            >
              <item.icon size={20} strokeWidth={2} />
              {item.label}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/admin"
              data-testid="side-nav-admin"
              className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900 transition-colors"
            >
              <LayoutDashboard size={20} /> Admin
            </NavLink>
          )}
        </nav>
        <div className="mt-4 space-y-2">
          {user?.plan !== "premium" && (
            <Button
              data-testid="side-upgrade-btn"
              onClick={() => navigate("/app/premium")}
              className="w-full rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white gap-2"
            >
              <Crown size={16} /> Go Premium
            </Button>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium text-zinc-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut size={20} /> Log out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-[260px]">
        <main className="mx-auto max-w-5xl px-4 pb-28 pt-4 sm:px-6 lg:pb-10 lg:pt-8">
          <Outlet />
          <footer className="mt-10 border-t border-zinc-200 pt-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <SocialShare />
              <p className="flex flex-wrap items-center justify-center gap-1 text-xs text-zinc-400">
                Powered &amp; developed with <Heart size={12} className="fill-red-500 text-red-500" /> by
                <span className="font-semibold text-zinc-600">Susttify Eco Innovation Ltd</span>
                &amp; <span className="font-semibold text-zinc-600">Jimtech Enterprise Solutions</span>
              </p>
            </div>
          </footer>
        </main>
      </div>

      {/* Floating Ask VecPadi (mobile) */}
      {!onAssistant && (
        <button
          data-testid="fab-assistant"
          onClick={() => navigate("/app/assistant")}
          className="lg:hidden fixed bottom-24 right-4 z-40 grid h-14 w-14 place-items-center rounded-full bg-emerald-500 text-white shadow-lg shadow-emerald-500/40 active:scale-95 transition-transform"
          aria-label="Ask VecPadi"
        >
          <MessageCircle size={24} />
        </button>
      )}

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-zinc-200 bg-white/90 backdrop-blur-xl pb-[env(safe-area-inset-bottom)]">
        <div className="grid grid-cols-5">
          {bottomNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-1 py-2.5 text-[11px] font-medium transition-colors ${
                  isActive ? "text-emerald-600" : "text-zinc-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon size={22} strokeWidth={isActive ? 2.4 : 2} />
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
